import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Download, PlusCircle, Search, Trash2, Clock, Filter, ArrowRight, Package, Calendar, MapPin, Truck, CheckCircle2, AlertCircle, MoreVertical, FileText, Edit2, Check, X, LayoutGrid, List, FileSpreadsheet } from 'lucide-react';
import * as XLSX from 'xlsx';


import { useAppContext } from '../../hooks/useAppContext';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { WorkflowRequest } from '../../types';
import { WORKFLOW_STAGES } from '../../data/mockData';
import { Input } from '../../components/ui/Input';
import useAppStore from '../../store/useAppStore';
import { differenceInHours, differenceInDays } from 'date-fns';
import toast from 'react-hot-toast';
import WorkflowRequestModal from './WorkflowRequestModal';

interface WorkflowScreenProps {
    // Props removed
}

const priorityVariant: { [key: string]: 'destructive' | 'warning' | 'default' } = {
    'عالية': 'destructive',
    'متوسطة': 'warning',
    'منخفضة': 'default',
}

// Helper function for precise time ago formatting
const timeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (seconds < 30) return `منذ بضع ثوان`;

    let interval = seconds / 31536000;
    if (interval > 1) return `منذ ${Math.floor(interval)} سنوات`;
    interval = seconds / 2592000;
    if (interval > 1) return `منذ ${Math.floor(interval)} أشهر`;
    interval = seconds / 86400;
    if (interval > 1) return `منذ ${Math.floor(interval)} أيام`;
    interval = seconds / 3600;
    if (interval > 1) return `منذ ${Math.floor(interval)} ساعات`;
    interval = seconds / 60;
    if (interval > 1) return `منذ ${Math.floor(interval)} دقائق`;
    return `منذ ${Math.floor(seconds)} ثوان`;
};


const LastUpdatedBadge: React.FC<{ date: string }> = ({ date }) => {
    const now = new Date();
    const modifiedDate = new Date(date);
    const hoursDiff = differenceInHours(now, modifiedDate);

    let colorClass = 'bg-slate-800/50 text-slate-400 border-slate-700';
    let pulseClass = '';

    if (hoursDiff < 24) {
        colorClass = 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20';
        pulseClass = 'animate-pulse-green';
    } else if (hoursDiff < 168) { // Less than 7 days
        colorClass = 'bg-amber-500/10 text-amber-400 border-amber-500/20';
    }

    return (
        <div className={`text-xs flex items-center gap-1.5 px-2 py-1 rounded-full border ${colorClass}`}>
            <div className={`w-1.5 h-1.5 rounded-full ${pulseClass} ${hoursDiff < 24 ? 'bg-emerald-500' : hoursDiff < 168 ? 'bg-amber-500' : 'bg-slate-500'}`}></div>
            <Clock size={10} />
            <span>{timeAgo(date)}</span>
        </div>
    );
};

const MiniDepartureCountdown: React.FC<{ request: WorkflowRequest }> = ({ request }) => {
    if (request.currentStageId <= 2 || !request.expectedDepartureDate || isNaN(new Date(request.expectedDepartureDate).getTime())) {
        return null;
    }

    const now = new Date();
    const endDate = new Date(request.expectedDepartureDate);

    if (now > endDate) return null;

    const daysLeft = differenceInDays(endDate, now);

    let colorClass = 'text-emerald-500';
    if (daysLeft <= 3) {
        colorClass = 'text-red-500';
    } else if (daysLeft <= 7) {
        colorClass = 'text-amber-500';
    }

    const tooltipText = `يغادر خلال: ${daysLeft} يوم`;

    const radius = 8;
    const circumference = 2 * Math.PI * radius;
    // We don't have start date here, so we fake progress based on a 30-day window for visual effect
    const progress = Math.max(0, 1 - (daysLeft / 30));
    const strokeDashoffset = circumference - progress * circumference;

    return (
        <div className="absolute top-4 left-4 z-10" title={tooltipText}>
            <div className="relative flex items-center justify-center w-8 h-8 bg-slate-900/80 backdrop-blur rounded-full border border-slate-700 shadow-lg">
                <svg className="w-5 h-5 transform -rotate-90" viewBox="0 0 20 20">
                    <circle
                        className="text-slate-800"
                        stroke="currentColor" strokeWidth="2" fill="transparent" r={radius} cx="10" cy="10"
                    />
                    <circle
                        className={`${colorClass} transition-all duration-1000 ease-out`}
                        stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="transparent" r={radius} cx="10" cy="10"
                        strokeDasharray={circumference}
                        strokeDashoffset={strokeDashoffset}
                    />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                    <Truck size={10} className="text-slate-300" />
                </div>
            </div>
        </div>
    );
};

// Marker used to store and parse the expected arrival date inside stage history comments
const ARRIVAL_MARKER = '[ARRIVAL_DATE]';

const getArrivalDateFromHistory = (request: WorkflowRequest): string => {
    if (!request.stageHistory || request.stageHistory.length === 0) return '';
    for (let i = request.stageHistory.length - 1; i >= 0; i--) {
        const item = request.stageHistory[i];
        if (item.comment && item.comment.startsWith(ARRIVAL_MARKER)) {
            return item.comment.replace(ARRIVAL_MARKER, '').trim();
        }
    }
    return '';
};

const upsertArrivalDateInHistory = (request: WorkflowRequest, date: string, processor: string): WorkflowRequest => {
    const currentStage = WORKFLOW_STAGES.find(s => s.id === request.currentStageId);
    const nowIso = new Date().toISOString();
    const newHistory = [...(request.stageHistory || [])];

    // Try to update the latest arrival marker if exists, else push a new history item
    let updated = false;
    for (let i = newHistory.length - 1; i >= 0; i--) {
        const item = newHistory[i];
        if (item.comment && item.comment.startsWith(ARRIVAL_MARKER)) {
            newHistory[i] = { ...item, comment: `${ARRIVAL_MARKER}${date}`, modified: { processor, timestamp: nowIso } };
            updated = true;
            break;
        }
    }

    if (!updated) {
        newHistory.push({
            stageId: request.currentStageId,
            stageName: currentStage?.name || `مرحلة ${request.currentStageId}`,
            processor,
            timestamp: nowIso,
            comment: `${ARRIVAL_MARKER}${date}`,
            documents: [],
        });
    }

    return {
        ...request,
        stageHistory: newHistory,
        lastModified: nowIso,
    };
};


const ModernWorkflowCard: React.FC<{
    request: WorkflowRequest;
    onViewDetails: () => void;
    onDelete: () => void;
}> = ({ request, onViewDetails, onDelete }) => {
    const { updateRequest } = useAppStore();
    const { user } = useAppContext();
    const currentStage = WORKFLOW_STAGES.find(s => s.id === request.currentStageId);
    const arrivalDate = request.expectedArrivalDate || getArrivalDateFromHistory(request);

    const [isEditing, setIsEditing] = useState(false);
    const [editTitle, setEditTitle] = useState(request.title);

    const handleSaveTitle = async (e: React.MouseEvent) => {
        e.stopPropagation();
        if (editTitle.trim() === '' || editTitle === request.title) {
            setIsEditing(false);
            setEditTitle(request.title);
            return;
        }
        try {
            await updateRequest({ ...request, title: editTitle });
            toast.success('تم تغيير اسم الطلب بنجاح');
            setIsEditing(false);
        } catch (error) {
            toast.error('حدث خطأ أثناء تغيير الاسم');
        }
    };

    const handleCancelEdit = (e: React.MouseEvent) => {
        e.stopPropagation();
        setEditTitle(request.title);
        setIsEditing(false);
    };

    return (
        <div
            onClick={onViewDetails}
            className="group relative glass-card p-5 rounded-2xl hover:bg-slate-800/50 transition-all duration-300 cursor-pointer border border-slate-700/50 hover:border-blue-500/30 overflow-hidden"
        >
            <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-blue-500 to-purple-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

            <MiniDepartureCountdown request={request} />

            <div className="flex justify-between items-start mb-4 pl-8">
                <div className="flex items-start gap-3">
                    <div className={`p-2.5 rounded-xl ${request.type === 'استيراد' ? 'bg-blue-500/10 text-blue-400' : 'bg-purple-500/10 text-purple-400'}`}>
                        {request.type === 'استيراد' ? <Download size={20} /> : <Truck size={20} />}
                    </div>
                    <div>
                        {isEditing ? (
                            <div className="flex items-center gap-2 mb-1" onClick={e => e.stopPropagation()}>
                                <Input
                                    value={editTitle}
                                    onChange={(e) => setEditTitle(e.target.value)}
                                    className="h-8 py-1 px-2 text-sm bg-slate-800 border-slate-600 text-white w-48"
                                    autoFocus
                                />
                                <button
                                    onClick={handleSaveTitle}
                                    className="p-1.5 rounded-lg bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 transition-colors"
                                >
                                    <Check size={14} />
                                </button>
                                <button
                                    onClick={handleCancelEdit}
                                    className="p-1.5 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors"
                                >
                                    <X size={14} />
                                </button>
                            </div>
                        ) : (
                            <div className="flex items-center gap-2 group/title">
                                <h3 className="font-bold text-white text-lg leading-tight mb-1 group-hover:text-blue-400 transition-colors">{request.title}</h3>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setIsEditing(true);
                                    }}
                                    className="opacity-0 group-hover/title:opacity-100 p-1 rounded hover:bg-slate-700 text-slate-400 hover:text-white transition-all"
                                >
                                    <Edit2 size={12} />
                                </button>
                            </div>
                        )}
                        <div className="flex items-center gap-2 text-xs text-slate-400 font-mono">
                            <span>{request.id}</span>
                            <span className="w-1 h-1 rounded-full bg-slate-600"></span>
                            <span>{new Date(request.creationDate).toLocaleDateString('en-GB')}</span>
                        </div>
                    </div>
                </div>

                <div className="flex flex-col items-end gap-2">
                    <Badge variant={priorityVariant[request.priority]} className="shadow-sm">{request.priority}</Badge>
                </div>
            </div>

            <div className="space-y-4 mb-4">
                <div className="bg-slate-900/50 rounded-xl p-3 border border-slate-700/30">
                    <div className="flex justify-between items-center mb-2">
                        <span className="text-xs text-slate-400">المرحلة الحالية</span>
                        <span className="text-xs font-semibold text-blue-400">{currentStage?.name}</span>
                    </div>

                    {/* 7-Stage Progress Indicator */}
                    <div className="flex gap-1.5 mt-2">
                        {WORKFLOW_STAGES.map((stage) => {
                            const isCompleted = request.currentStageId > stage.id;
                            const isCurrent = request.currentStageId === stage.id;

                            let bgClass = 'bg-slate-700';
                            if (isCompleted) bgClass = 'bg-blue-500';
                            if (isCurrent) bgClass = 'bg-blue-400 animate-pulse';

                            return (
                                <div
                                    key={stage.id}
                                    className={`h-1.5 flex-1 rounded-full transition-all duration-300 ${bgClass}`}
                                    title={stage.name}

                                />
                            );
                        })}
                    </div>

                    <div className="flex justify-between mt-1">
                        <span className="text-[10px] text-slate-500">البداية</span>
                        <span className="text-[10px] text-slate-500">النهاية</span>
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="bg-slate-800/30 p-2 rounded-lg border border-slate-700/30 flex items-center gap-2">
                        <Calendar size={12} className="text-slate-500" />
                        <div>
                            <div className="text-slate-500 text-[10px]">المغادرة</div>
                            <div className="font-mono text-slate-300">{request.expectedDepartureDate ? new Date(request.expectedDepartureDate).toLocaleDateString('en-GB') : '-'}</div>
                        </div>
                    </div>
                    <div className="bg-slate-800/30 p-2 rounded-lg border border-slate-700/30 flex items-center gap-2">
                        <MapPin size={12} className="text-slate-500" />
                        <div>
                            <div className="text-slate-500 text-[10px]">الوصول</div>
                            <div className="font-mono text-slate-300">{arrivalDate ? new Date(arrivalDate).toLocaleDateString('en-GB') : '-'}</div>
                        </div>
                    </div>
                </div>
                {/* BL Details: Number & Date */}
                <div className="grid grid-cols-2 gap-2 mb-2 text-xs">
                    <div className="bg-slate-800/30 p-2 rounded-lg border border-slate-700/30 flex items-center gap-2">
                        <FileText size={12} className="text-blue-400" />
                        <div>
                            <div className="text-slate-500 text-[10px]">رقم البوليصة (BL)</div>
                            <div className="font-mono text-white font-bold">{request.blNumber || '---'}</div>
                        </div>
                    </div>
                    <div className="bg-slate-800/30 p-2 rounded-lg border border-slate-700/30 flex items-center gap-2">
                        <Calendar size={12} className="text-blue-400" />
                        <div>
                            <div className="text-slate-500 text-[10px]">تاريخ البوليصة</div>
                            <div className="font-mono text-white font-bold">{request.blDate ? new Date(request.blDate).toLocaleDateString('en-GB') : '---'}</div>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-2 mb-4">
                    {/* Invoice Number Display */}
                    <div className="bg-slate-800/30 p-2 rounded-lg border border-slate-700/30 flex items-center gap-2">
                        <FileText size={12} className="text-emerald-400" />
                        <div>
                            <div className="text-slate-500 text-[10px]">رقم الفاتورة</div>
                            <div className="font-mono text-white font-bold">{request.invoiceNumber || '---'}</div>
                        </div>
                    </div>
                    {/* Goods Type Display */}
                    <div className="bg-slate-800/30 p-2 rounded-lg border border-slate-700/30 flex items-center gap-2">
                        <Package size={12} className="text-purple-400" />
                        <div>
                            <div className="text-slate-500 text-[10px]">نوع البضاعة</div>
                            <div className="font-mono text-white font-bold">{request.goodsType || '---'}</div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="flex items-center justify-between pt-3 border-t border-slate-700/30">
                {request.lastModified && !isNaN(new Date(request.lastModified).getTime()) ? (
                    <LastUpdatedBadge date={request.lastModified} />
                ) : (
                    <span></span>
                )}

                <div className="flex items-center gap-2">
                    <button
                        onClick={(e) => { e.stopPropagation(); onDelete(); }}
                        className="p-2 rounded-lg text-slate-400 hover:bg-red-500/10 hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100"
                        title="حذف الطلب"
                    >
                        <Trash2 size={16} />
                    </button>
                    <button className="flex items-center gap-1 text-xs font-semibold text-blue-400 hover:text-blue-300 transition-colors">
                        التفاصيل <ArrowRight size={14} className="rotate-180" />
                    </button>
                </div>
            </div>
        </div >
    )
}

const ModernWorkflowScreen: React.FC<WorkflowScreenProps> = () => {
    const { t, user } = useAppContext();
    const { requests, isWorkflowModalOpen, setWorkflowModalOpen, deleteRequest, openConfirmation } = useAppStore();
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState('');
    const [typeFilter, setTypeFilter] = useState('all');
    const [stageFilter, setStageFilter] = useState('all');
    const [priorityFilter, setPriorityFilter] = useState('all');
    const [companyFilter, setCompanyFilter] = useState('all');
    const [viewMode, setViewMode] = useState<'grid' | 'list'>(() =>
        (localStorage.getItem('workflow_view_mode') as 'grid' | 'list') || 'grid'
    );

    // ── Fixed list of approved supplier companies ──
    const APPROVED_COMPANIES = [
        'AoyWell', 'ASTRONERGY', 'ENERGET', 'Hoymiles',
        'INVT', 'KSENG', 'Ritar', 'SunTree',
    ];

    const toggleViewMode = (mode: 'grid' | 'list') => {
        setViewMode(mode);
        localStorage.setItem('workflow_view_mode', mode);
    };

    const exportToExcel = () => {
        const headers = [
            '#',
            'اسم الطلب',
            'رقم الطلب',
            'النوع',
            'الأولوية',
            'المرحلة الحالية',
            'المغادرة',
            'الوصول',
            'رقم BL',
            'تاريخ BL',
            'رقم الفاتورة',
            'نوع البضاعة',
            'حاويات 20',
            'حاويات 40',
        ];

        const rows = filteredRequests.map((req, idx) => {
            const currentStage = WORKFLOW_STAGES.find(s => s.id === req.currentStageId);
            const arrivalDate = req.expectedArrivalDate || getArrivalDateFromHistory(req);
            return [
                idx + 1,
                req.title,
                req.id,
                req.type,
                req.priority,
                currentStage?.name || '—',
                req.expectedDepartureDate
                    ? new Date(req.expectedDepartureDate).toLocaleDateString('en-GB')
                    : '—',
                arrivalDate
                    ? new Date(arrivalDate).toLocaleDateString('en-GB')
                    : '—',
                req.blNumber   || '—',
                req.blDate
                    ? new Date(req.blDate).toLocaleDateString('en-GB')
                    : '—',
                req.ciNumber   || '—',
                req.goodsType  || '—',
                req.containerCount20ft ?? '—',
                req.containerCount40ft ?? '—',
            ];
        });

        const colCount = headers.length;

        // ── Build AOA: title row, header row, data rows ──
        const sheetData: (string | number)[][] = [
            ['قائمة الاستيراد والتصدير'],
            headers,
            ...rows,
        ];

        const ws = XLSX.utils.aoa_to_sheet(sheetData);

        // ── Merge title across all columns ──
        ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: colCount - 1 } }];

        // ── AutoFilter on header row (row 2 = index 1) ──
        ws['!autofilter'] = { ref: `A2:${XLSX.utils.encode_col(colCount - 1)}2` };

        // ── Freeze panes: keep title + header always visible ──
        ws['!freeze'] = { xSplit: 0, ySplit: 2 };

        // ── Compact column widths (total ≈ fits A4 landscape) ──
        ws['!cols'] = [
            { wch: 4  },  // #
            { wch: 22 },  // اسم الطلب
            { wch: 18 },  // رقم الطلب
            { wch: 9  },  // النوع
            { wch: 9  },  // الأولوية
            { wch: 18 },  // المرحلة
            { wch: 11 },  // المغادرة
            { wch: 11 },  // الوصول
            { wch: 18 },  // رقم BL
            { wch: 11 },  // تاريخ BL
            { wch: 18 },  // رقم الفاتورة
            { wch: 14 },  // نوع البضاعة
            { wch: 9  },  // حاويات 20
            { wch: 9  },  // حاويات 40
        ];

        // ── Row heights ──
        ws['!rows'] = [
            { hpt: 36 },  // title
            { hpt: 30 },  // headers
            ...Array(rows.length).fill({ hpt: 20 }),
        ];

        // ── Page setup: landscape, fit to 1 page wide ──
        ws['!pageSetup'] = {
            orientation: 'landscape',
            fitToPage: true,
            fitToWidth: 1,
            fitToHeight: 0,
            paperSize: 9, // A4
        };

        // ── Print area ──
        ws['!printOptions'] = { gridLines: false };

        // ── Title cell style ──
        const titleCell = ws['A1'];
        if (titleCell) {
            titleCell.s = {
                font: { bold: true, sz: 16, color: { rgb: 'FFFFFF' } },
                fill: { fgColor: { rgb: '1E3A5F' } },
                alignment: { horizontal: 'center', vertical: 'center', readingOrder: 2 },
                border: { bottom: { style: 'medium', color: { rgb: '3B82F6' } } },
            };
        }

        // ── Header row styles ──
        for (let c = 0; c < colCount; c++) {
            const ref = XLSX.utils.encode_cell({ r: 1, c });
            if (!ws[ref]) continue;
            ws[ref].s = {
                font: { bold: true, sz: 10, color: { rgb: 'FFFFFF' } },
                fill: { fgColor: { rgb: '1D4ED8' } },
                alignment: { horizontal: 'center', vertical: 'center', readingOrder: 2, wrapText: true },
                border: {
                    top:    { style: 'thin',   color: { rgb: '60A5FA' } },
                    bottom: { style: 'medium', color: { rgb: '93C5FD' } },
                    left:   { style: 'thin',   color: { rgb: '60A5FA' } },
                    right:  { style: 'thin',   color: { rgb: '60A5FA' } },
                },
            };
        }

        // ── Data row styles ──
        for (let r = 0; r < rows.length; r++) {
            const bgColor = r % 2 === 0 ? 'FFFFFF' : 'EFF6FF';
            for (let c = 0; c < colCount; c++) {
                const ref = XLSX.utils.encode_cell({ r: 2 + r, c });
                if (!ws[ref]) continue;
                const v = ws[ref].v;
                const txtColor =
                    v === 'استيراد' ? '1D4ED8' :
                    v === 'تصدير'   ? '7C3AED' :
                    v === 'عالية'   ? 'DC2626' :
                    v === 'متوسطة'  ? 'D97706' :
                                     '1E293B';
                ws[ref].s = {
                    font: { sz: 10, bold: c === 0, color: { rgb: txtColor } },
                    fill: { fgColor: { rgb: bgColor } },
                    alignment: {
                        horizontal: c === 1 ? 'right' : 'center',  // name column right-aligned for Arabic
                        vertical: 'center',
                        readingOrder: 2,
                        wrapText: true,
                    },
                    border: {
                        top:    { style: 'thin', color: { rgb: 'DBEAFE' } },
                        bottom: { style: 'thin', color: { rgb: 'DBEAFE' } },
                        left:   { style: 'thin', color: { rgb: 'DBEAFE' } },
                        right:  { style: 'thin', color: { rgb: 'DBEAFE' } },
                    },
                };
            }
        }

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'الاستيراد والتصدير');

        const today = new Date().toISOString().slice(0, 10);
        XLSX.writeFile(wb, `قائمة_الاستيراد_والتصدير_${today}.xlsx`);
        toast.success('تم تصدير القائمة بنجاح ✅');
    };

    const handleDeleteRequest = (requestId: string) => {
        if (!user) return;
        openConfirmation(
            'هل أنت متأكد من حذف هذا الطلب؟ لا يمكن التراجع عن هذا الإجراء.',
            () => deleteRequest(requestId, user.employeeId)
        );
    };

    const filteredRequests = requests.filter(req => {
        const q = searchTerm.toLowerCase().trim();
        const stage = WORKFLOW_STAGES.find(s => s.id === req.currentStageId);
        const matchesSearch = q === '' || [
            req.title,
            req.id,
            req.type,
            req.priority,
            req.blNumber,
            req.ciNumber,
            req.goodsType,
            req.supplierInfo?.name,
            req.trackingNumber,
            req.departurePort,
            stage?.name,
        ].some(v => v && String(v).toLowerCase().includes(q));
        const matchesType = typeFilter === 'all' || req.type === typeFilter;
        const matchesStage = stageFilter === 'all' || req.currentStageId.toString() === stageFilter;
        const matchesPriority = priorityFilter === 'all' || req.priority === priorityFilter;
        const matchesCompany = companyFilter === 'all' ||
            req.supplierInfo?.name?.trim().toLowerCase() === companyFilter.toLowerCase();
        return matchesSearch && matchesType && matchesStage && matchesPriority && matchesCompany;
    });

    return (
        <div className="max-w-7xl mx-auto space-y-8 animate-content-fade-in pb-20">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate(-1)}
                        className="p-2 rounded-xl bg-slate-800/50 hover:bg-slate-700 text-slate-400 hover:text-white transition-all border border-slate-700/50"
                    >
                        <ArrowRight size={20} />
                    </button>
                    <div>
                        <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
                            <div className="p-3 rounded-2xl bg-gradient-to-br from-blue-600 to-purple-600 shadow-lg shadow-blue-500/20">
                                <Package size={28} className="text-white" />
                            </div>
                            {t('importExport')}
                        </h1>
                        <p className="text-slate-400">إدارة ومتابعة طلبات الاستيراد والتصدير وسير العمل</p>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    {/* Excel Export Button — hidden for now, re-enable later */}
                    {/* <button
                        onClick={exportToExcel}
                        disabled={filteredRequests.length === 0}
                        className="flex items-center gap-2 px-5 py-3 rounded-xl border border-emerald-500/30 bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 hover:border-emerald-500/50 transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
                        title="تصدير إلى Excel"
                    >
                        <FileSpreadsheet size={20} />
                        <span className="text-sm font-medium">تصدير Excel</span>
                    </button> */}

                    {/* New Request Button */}
                    <button
                        onClick={() => setWorkflowModalOpen(true)}
                        className="flex items-center gap-2 px-6 py-3 rounded-xl shadow-lg shadow-emerald-500/20 hover:shadow-emerald-500/40 transition-all bg-gradient-to-r from-emerald-500 to-teal-600 text-white hover:from-emerald-600 hover:to-teal-700"
                    >
                        <PlusCircle size={20} />
                        <span>{t('createNewRequest')}</span>
                    </button>
                </div>
            </div>

            {/* Filters */}
            <div className="glass-panel p-4 rounded-2xl flex flex-col gap-4">
                {/* Search + Dropdowns + Toggle */}
                <div className="flex flex-col lg:flex-row gap-4 items-center">
                <div className="relative flex-1 w-full">
                    <Search size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                        type="text"
                        placeholder="بحث باسم الطلب، رقم BL، الشركة، نوع البضاعة..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full bg-slate-900/50 border border-slate-700 rounded-xl py-2.5 pr-10 pl-4 text-white placeholder:text-slate-500 focus:outline-none focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/50 transition-all"
                    />
                    {searchTerm && (
                        <button
                            onClick={() => setSearchTerm('')}
                            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                        >
                            <X size={15} />
                        </button>
                    )}
                </div>
                <div className="flex gap-2 w-full lg:w-auto overflow-x-auto pb-2 lg:pb-0 items-center">
                    <select
                        value={typeFilter}
                        onChange={(e) => setTypeFilter(e.target.value)}
                        className="bg-slate-900/50 border border-slate-700 rounded-xl py-2.5 px-4 text-slate-300 focus:outline-none focus:border-blue-500/50 transition-all min-w-[120px]"
                    >
                        <option value="all">كل الأنواع</option>
                        <option value="استيراد">استيراد</option>
                        <option value="تصدير">تصدير</option>
                    </select>
                    <select
                        value={stageFilter}
                        onChange={(e) => setStageFilter(e.target.value)}
                        className="bg-slate-900/50 border border-slate-700 rounded-xl py-2.5 px-4 text-slate-300 focus:outline-none focus:border-blue-500/50 transition-all min-w-[140px]"
                    >
                        <option value="all">كل المراحل</option>
                        {WORKFLOW_STAGES.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                    <select
                        value={priorityFilter}
                        onChange={(e) => setPriorityFilter(e.target.value)}
                        className="bg-slate-900/50 border border-slate-700 rounded-xl py-2.5 px-4 text-slate-300 focus:outline-none focus:border-blue-500/50 transition-all min-w-[120px]"
                    >
                        <option value="all">كل الأولويات</option>
                        <option value="عالية">عالية</option>
                        <option value="متوسطة">متوسطة</option>
                        <option value="منخفضة">منخفضة</option>
                    </select>

                    {/* View Mode Toggle */}
                    <div className="flex items-center gap-1 bg-slate-900/50 border border-slate-700 rounded-xl p-1 flex-shrink-0">
                        <button
                            onClick={() => toggleViewMode('grid')}
                            title="عرض مربعات"
                            className={`p-2 rounded-lg transition-all duration-200 ${
                                viewMode === 'grid'
                                    ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                                    : 'text-slate-500 hover:text-slate-300 hover:bg-white/5'
                            }`}
                        >
                            <LayoutGrid size={17} />
                        </button>
                        <button
                            onClick={() => toggleViewMode('list')}
                            title="عرض قائمة"
                            className={`p-2 rounded-lg transition-all duration-200 ${
                                viewMode === 'list'
                                    ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                                    : 'text-slate-500 hover:text-slate-300 hover:bg-white/5'
                            }`}
                        >
                            <List size={17} />
                        </button>
                    </div>
                </div>
                </div>

                {/* ── Company Filter Chips ── */}
                <div className="flex flex-wrap gap-2 items-center pt-1">
                    <span className="text-xs text-slate-500 flex-shrink-0 font-medium">الشركات:</span>

                    {/* All chip */}
                    <button
                        onClick={() => setCompanyFilter('all')}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all duration-200 ${
                            companyFilter === 'all'
                                ? 'bg-blue-500/20 text-blue-400 border-blue-500/40 shadow-sm shadow-blue-500/10'
                                : 'bg-slate-800/50 text-slate-400 border-slate-700/50 hover:border-slate-600 hover:text-slate-300'
                        }`}
                    >
                        الكل
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${
                            companyFilter === 'all' ? 'bg-blue-500/30' : 'bg-slate-700'
                        }`}>{filteredRequests.length}</span>
                    </button>

                    {APPROVED_COMPANIES.map(company => {
                        const count = requests.filter(r =>
                            r.supplierInfo?.name?.trim().toLowerCase() === company.toLowerCase()
                        ).length;
                        const isActive = companyFilter === company;
                        return (
                            <button
                                key={company}
                                onClick={() => setCompanyFilter(isActive ? 'all' : company)}
                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all duration-200 ${
                                    isActive
                                        ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/40 shadow-sm shadow-emerald-500/10 scale-105'
                                        : count > 0
                                            ? 'bg-slate-800/60 text-slate-300 border-slate-600/60 hover:border-emerald-500/30 hover:text-emerald-400 hover:bg-emerald-500/5'
                                            : 'bg-slate-900/30 text-slate-600 border-slate-800/50 cursor-default'
                                }`}
                            >
                                {company}
                                {count > 0 && (
                                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${
                                        isActive ? 'bg-emerald-500/30 text-emerald-300' : 'bg-slate-700 text-slate-400'
                                    }`}>{count}</span>
                                )}
                            </button>
                        );
                    })}

                    {companyFilter !== 'all' && (
                        <button
                            onClick={() => setCompanyFilter('all')}
                            className="flex items-center gap-1 text-xs text-slate-500 hover:text-rose-400 transition-colors px-2 py-1 rounded-full hover:bg-rose-500/10 border border-transparent hover:border-rose-500/20"
                        >
                            <X size={12} />
                            إلغاء
                        </button>
                    )}
                </div>
            </div>

            {/* Content */}
            {filteredRequests.length > 0 ? (
                viewMode === 'grid' ? (
                    /* ── Grid View ── */
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filteredRequests.map(req =>
                            <ModernWorkflowCard
                                key={req.id}
                                request={req}
                                onViewDetails={() => navigate(`/workflow/${req.id}`)}
                                onDelete={() => handleDeleteRequest(req.id)}
                            />
                        )}
                    </div>
                ) : (
                    /* ── List View ── */
                    <div className="glass-panel rounded-2xl overflow-hidden border border-slate-700/50">
                        {/* Table Header */}
                        <div className="hidden xl:grid grid-cols-[2fr_1fr_1fr_1.6fr_1fr_1fr_1.1fr_1fr_1fr_1fr_1fr_auto] gap-2 px-5 py-3 bg-slate-800/60 border-b border-slate-700/50 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                            <span>الطلب</span>
                            <span>النوع</span>
                            <span>الأولوية</span>
                            <span>المرحلة</span>
                            <span>المغادرة</span>
                            <span>الوصول</span>
                            <span>رقم BL</span>
                            <span>تاريخ BL</span>
                            <span>رقم الفاتورة</span>
                            <span>نوع البضاعة</span>
                            <span>الشركة</span>
                            <span></span>
                        </div>

                        {/* Rows */}
                        <div className="divide-y divide-slate-700/30">
                            {filteredRequests.map((req, idx) => {
                                const currentStage = WORKFLOW_STAGES.find(s => s.id === req.currentStageId);
                                const priorityColor = req.priority === 'عالية'
                                    ? 'text-rose-400 bg-rose-500/10 border-rose-500/20'
                                    : req.priority === 'متوسطة'
                                    ? 'text-amber-400 bg-amber-500/10 border-amber-500/20'
                                    : 'text-slate-400 bg-slate-500/10 border-slate-500/20';
                                const arrivalDate = req.expectedArrivalDate || getArrivalDateFromHistory(req);
                                return (
                                    <div
                                        key={req.id}
                                        onClick={() => navigate(`/workflow/${req.id}`)}
                                        className="grid grid-cols-1 xl:grid-cols-[2fr_1fr_1fr_1.6fr_1fr_1fr_1.1fr_1fr_1fr_1fr_1fr_auto] gap-2 items-center px-5 py-4 cursor-pointer group hover:bg-white/5 transition-all duration-200"
                                        style={{ animationDelay: `${idx * 25}ms` }}
                                    >
                                        {/* Title + ID */}
                                        <div className="flex items-center gap-2 min-w-0">
                                            <div className={`p-1.5 rounded-lg flex-shrink-0 ${
                                                req.type === 'استيراد' ? 'bg-blue-500/10 text-blue-400' : 'bg-purple-500/10 text-purple-400'
                                            }`}>
                                                {req.type === 'استيراد' ? <Download size={14} /> : <Truck size={14} />}
                                            </div>
                                            <div className="min-w-0">
                                                <p className="font-semibold text-white group-hover:text-blue-400 transition-colors truncate text-sm leading-tight">{req.title}</p>
                                                <p className="text-[10px] text-slate-500 font-mono">{req.id}</p>
                                            </div>
                                        </div>

                                        {/* Type */}
                                        <span className={`hidden xl:inline-flex text-[11px] font-medium px-2 py-0.5 rounded-full border w-fit ${
                                            req.type === 'استيراد' ? 'text-blue-400 bg-blue-500/10 border-blue-500/20' : 'text-purple-400 bg-purple-500/10 border-purple-500/20'
                                        }`}>{req.type}</span>

                                        {/* Priority */}
                                        <span className={`hidden xl:inline-flex text-[11px] font-medium px-2 py-0.5 rounded-full border w-fit ${priorityColor}`}>
                                            {req.priority}
                                        </span>

                                        {/* Stage */}
                                        <div className="hidden xl:flex flex-col gap-1">
                                            <span className="text-[11px] text-blue-400 font-medium leading-tight">{currentStage?.name}</span>
                                            <div className="flex gap-0.5">
                                                {WORKFLOW_STAGES.map(s => (
                                                    <div key={s.id} className={`h-1 flex-1 rounded-full ${
                                                        req.currentStageId > s.id ? 'bg-blue-500' : req.currentStageId === s.id ? 'bg-blue-400 animate-pulse' : 'bg-slate-700'
                                                    }`} />
                                                ))}
                                            </div>
                                        </div>

                                        {/* المغادرة */}
                                        <div className="hidden xl:flex flex-col">
                                            <span className="text-[10px] text-slate-500">المغادرة</span>
                                            <span className="text-xs text-slate-300 font-mono">
                                                {req.expectedDepartureDate
                                                    ? new Date(req.expectedDepartureDate).toLocaleDateString('en-GB')
                                                    : <span className="text-slate-600">—</span>}
                                            </span>
                                        </div>

                                        {/* الوصول */}
                                        <div className="hidden xl:flex flex-col">
                                            <span className="text-[10px] text-slate-500">الوصول</span>
                                            <span className="text-xs text-slate-300 font-mono">
                                                {arrivalDate
                                                    ? new Date(arrivalDate).toLocaleDateString('en-GB')
                                                    : <span className="text-slate-600">—</span>}
                                            </span>
                                        </div>

                                        {/* رقم BL */}
                                        <div className="hidden xl:flex flex-col">
                                            <span className="text-[10px] text-slate-500">رقم BL</span>
                                            <span className="text-xs font-bold text-white font-mono">
                                                {req.blNumber || <span className="text-slate-600 font-normal">—</span>}
                                            </span>
                                        </div>

                                        {/* تاريخ BL */}
                                        <div className="hidden xl:flex flex-col">
                                            <span className="text-[10px] text-slate-500">تاريخ BL</span>
                                            <span className="text-xs text-slate-300 font-mono">
                                                {req.blDate
                                                    ? new Date(req.blDate).toLocaleDateString('en-GB')
                                                    : <span className="text-slate-600">—</span>}
                                            </span>
                                        </div>

                                        {/* رقم الفاتورة */}
                                        <div className="hidden xl:flex flex-col">
                                            <span className="text-[10px] text-slate-500">رقم الفاتورة</span>
                                            <span className="text-xs font-bold text-emerald-400 font-mono">
                                                {req.ciNumber || <span className="text-slate-600 font-normal">—</span>}
                                            </span>
                                        </div>

                                        {/* نوع البضاعة */}
                                        <div className="hidden xl:flex flex-col">
                                            <span className="text-[10px] text-slate-500">نوع البضاعة</span>
                                            <span className="text-xs font-bold text-purple-300 truncate">
                                                {req.goodsType || <span className="text-slate-600 font-normal">—</span>}
                                            </span>
                                        </div>

                                        {/* الشركة */}
                                        <div className="hidden xl:flex flex-col">
                                            <span className="text-[10px] text-slate-500">الشركة</span>
                                            <span className="text-xs font-semibold text-emerald-400 truncate">
                                                {req.supplierInfo?.name || <span className="text-slate-600 font-normal">—</span>}
                                            </span>
                                        </div>

                                        {/* Actions */}
                                        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <button
                                                onClick={(e) => { e.stopPropagation(); handleDeleteRequest(req.id); }}
                                                className="p-1.5 rounded-lg hover:bg-rose-500/20 text-slate-500 hover:text-rose-400 transition-colors"
                                                title="حذف"
                                            >
                                                <Trash2 size={15} />
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Footer */}
                        <div className="px-5 py-3 bg-slate-800/40 border-t border-slate-700/50 flex items-center justify-between text-xs text-slate-500">
                            <span>إجمالي الطلبات: <span className="text-blue-400 font-semibold">{filteredRequests.length}</span></span>
                            <div className="flex gap-4">
                                <span>استيراد: <span className="text-blue-400 font-semibold">{filteredRequests.filter(r => r.type === 'استيراد').length}</span></span>
                                <span>تصدير: <span className="text-purple-400 font-semibold">{filteredRequests.filter(r => r.type === 'تصدير').length}</span></span>
                            </div>
                        </div>
                    </div>
                )
            ) : (
                <div className="flex flex-col items-center justify-center py-20 text-center">
                    <div className="w-24 h-24 bg-slate-800/50 rounded-full flex items-center justify-center mb-6 animate-pulse">
                        <Package size={48} className="text-slate-600" />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2">لا توجد طلبات</h3>
                    <p className="text-slate-400 max-w-md mx-auto mb-8">
                        {searchTerm || typeFilter !== 'all' || stageFilter !== 'all' || priorityFilter !== 'all' || companyFilter !== 'all'
                            ? 'لا توجد نتائج تطابق معايير البحث الخاصة بك.'
                            : 'لم يتم إنشاء أي طلبات استيراد أو تصدير بعد. ابدأ بإنشاء طلب جديد.'}
                    </p>
                    <button
                        onClick={() => setWorkflowModalOpen(true)}
                        className="px-6 py-2.5 rounded-xl bg-slate-800 text-white hover:bg-slate-700 border border-slate-700 transition-all flex items-center gap-2"
                    >
                        <PlusCircle size={18} />
                        <span>إنشاء طلب جديد</span>
                    </button>
                </div>
            )}

            <WorkflowRequestModal isOpen={isWorkflowModalOpen} />
        </div>
    );
};

export default ModernWorkflowScreen;
